import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ManageCapTransactionsTableColumn } from '@dealstack/shared/lib/common/business/cap/manageCap';
import { useMemo } from 'react';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

export enum CAP_TRANSACTION_QUICK_FILTERS {
	NO_BUYER = 'NO_BUYER',
	NO_SELLER = 'NO_SELLER',
	NO_TYPE = 'NO_TYPE',
	INVALID = 'INVALID',
	REDEMPTION = 'REDEMPTION',
	NONE = 'NONE',
}

const QUICK_FILTER_MAPPING = {
	[CAP_TRANSACTION_QUICK_FILTERS.INVALID]: {
		id: ManageCapTransactionsTableColumn.isTransactionValid,
		value: false,
	},
	[CAP_TRANSACTION_QUICK_FILTERS.NO_BUYER]: {
		id: ManageCapTransactionsTableColumn.destinationPersonId,
		value: null,
	},
	[CAP_TRANSACTION_QUICK_FILTERS.NO_SELLER]: {
		id: ManageCapTransactionsTableColumn.sourcePersonId,
		value: null,
	},
	[CAP_TRANSACTION_QUICK_FILTERS.NO_TYPE]: {
		id: ManageCapTransactionsTableColumn.noType,
		value: null,
	},
	[CAP_TRANSACTION_QUICK_FILTERS.REDEMPTION]: {
		id: ManageCapTransactionsTableColumn.isPossibleRedemption,
		value: true,
	},
} as const;

export const useTransactionsQuickFilters = (
	mantineTableDataOperations: MantineTableDataOperations
) => {
	const tCapManage = useTranslationCapManage();

	const quickFilter = useMemo(() => {
		const currentFilters = mantineTableDataOperations.columnFilters;

		if (!currentFilters || currentFilters.length === 0) {
			return CAP_TRANSACTION_QUICK_FILTERS.NONE;
		}

		for (const [filterKey, filterConfig] of Object.entries(QUICK_FILTER_MAPPING)) {
			const hasMatchingFilter = currentFilters.some(
				(filter) => filter.id === filterConfig.id && filter.value === filterConfig.value
			);

			if (hasMatchingFilter) {
				return filterKey as CAP_TRANSACTION_QUICK_FILTERS;
			}
		}

		return CAP_TRANSACTION_QUICK_FILTERS.NONE;
	}, [mantineTableDataOperations.columnFilters]);

	const handleQuickFilter = (filter: CAP_TRANSACTION_QUICK_FILTERS) => {
		const isFilterActive = filter === quickFilter;

		if (isFilterActive) {
			return mantineTableDataOperations.setColumnFilters([]);
		}

		const filterConfig = QUICK_FILTER_MAPPING[filter];
		if (filterConfig) {
			return mantineTableDataOperations.setColumnFilters([filterConfig]);
		}

		return mantineTableDataOperations.setColumnFilters([]);
	};

	const quickFilters = [
		{
			title: tCapManage('transactions.custom_filters.no_buyer'),
			filter: CAP_TRANSACTION_QUICK_FILTERS.NO_BUYER,
		},
		{
			title: tCapManage('transactions.custom_filters.no_seller'),
			filter: CAP_TRANSACTION_QUICK_FILTERS.NO_SELLER,
		},
		{
			title: tCapManage('transactions.custom_filters.no_type'),
			filter: CAP_TRANSACTION_QUICK_FILTERS.NO_TYPE,
		},
		{
			title: tCapManage('transactions.custom_filters.invalid'),
			filter: CAP_TRANSACTION_QUICK_FILTERS.INVALID,
			dataTestId: TEST_ELEMENT_IDS.CAP_TRANSACTION_TABLE_INVALID_FILTER,
		},
	];

	const appliedFilterLabels = Object.fromEntries(
		quickFilters.map(({ title, filter }) => [QUICK_FILTER_MAPPING[filter].id, title])
	);

	return { quickFilter, handleQuickFilter, quickFilters, appliedFilterLabels };
};
